import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthenticateInput, AuthenticateResult, LoginInfoResult, RefreshTokenResult } from '../models/auth.model';
import { SessionService } from './session.service';
import { TenantService } from './tenant.service';

interface AbpResponse<T> {
  result: T;
  success: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly sessionService = inject(SessionService);
  private readonly tenantService = inject(TenantService);

  authenticate(input: AuthenticateInput): Observable<void> {
    return this.http
      .post<AbpResponse<AuthenticateResult>>(
        `${environment.apiUrl}/api/TokenAuth/Authenticate`,
        // rememberClient is hardcoded (not user-facing): the "keep me signed in" checkbox
        // was removed from the UI, but the app still relies on silent refresh-token renewal
        // for handheld operational continuity.
        { ...input, rememberClient: true },
      )
      .pipe(
        map((r) => r.result),
        tap((result) => this.sessionService.setSession(result)),
        switchMap(() =>
          this.http
            .get<AbpResponse<LoginInfoResult>>(
              `${environment.apiUrl}/api/services/app/Session/GetCurrentLoginInformations`,
            )
            .pipe(
              tap((r) => {
                const info = r.result;
                this.tenantService.setLogoId(info?.tenant?.logoId ?? null);
                const displayName = [info?.user?.name, info?.user?.surname]
                  .filter(Boolean)
                  .join(' ');
                if (displayName) this.sessionService.updateDisplayName(displayName);
              }),
              catchError(() => of(null)),
            ),
        ),
        map(() => void 0),
      );
  }

  tryRefreshToken(): Observable<boolean> {
    const refreshToken = this.sessionService.getRefreshToken();
    if (!refreshToken) return of(false);

    return this.http
      .post<AbpResponse<RefreshTokenResult>>(
        `${environment.apiUrl}/api/TokenAuth/RefreshToken`,
        null,
        { params: { refreshToken } },
      )
      .pipe(
        map((r) => r.result),
        tap((result) => this.sessionService.updateToken(result.accessToken, result.expireInSeconds)),
        map(() => true),
        catchError(() => of(false)),
      );
  }
}
